import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ENV from '../../env.json';
import Button from '../../components/button/button.index';
import MovieCard from '../../components/movieCard/movieCard.index';
import WOMPath from '../WOM.path';
import imageNotFound from '../../assets/images/image-not-found.svg';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CastType, MovieType } from '../WOM.interface';
import { errorMessages, WOMAPI } from '../WOM.api';
import { useParams, useHistory } from 'react-router';
import { makeApiUrl, responseValidator } from '../../scripts/functions';
import { Link } from 'react-router-dom';
import { LazyImage } from 'react-lazy-images';
import { toast } from 'react-toastify';
import 'swiper/swiper.scss';
import './movieDetail.style.scss';
SwiperCore.use([Autoplay]);

const MovieDetail: React.FC = () => {
    const [movie, setMovie] = useState<MovieType | undefined>(undefined);
    const [recommendations, setRecommendations] = useState<MovieType[] | undefined>(undefined);
    const [casts, setCasts] = useState<CastType[] | undefined>(undefined);
    const params: { id: string } = useParams();
    const history = useHistory();

    useEffect(() => {
        setMovie(undefined);
        setRecommendations(undefined);
        setCasts(undefined);
        axios.get<MovieType>(makeApiUrl(WOMAPI.movieDetail(Number(params.id)))).then((response) => {
            if (responseValidator(response)) {
                setMovie(response.data);
            } else {
                toast.error(errorMessages.APIError);
            }
        }).catch(() => {
            toast.error(errorMessages.APIError);
        });
        axios.get(makeApiUrl(WOMAPI.casts(Number(params.id)))).then((response) => {
            if (responseValidator(response)) {
                setCasts(response.data.cast);
            } else {
                toast.error(errorMessages.APIError);
            }
        }).catch(() => {
            toast.error(errorMessages.APIError);
        });
        axios.get(makeApiUrl(WOMAPI.recommendationMovies(Number(params.id)))).then((response) => {
            if (responseValidator(response)) {
                setRecommendations(response.data.results);
            } else {
                toast.error(errorMessages.APIError);
            }
        }).catch(() => {
            toast.error(errorMessages.APIError);
        });
    }, [history.location]);

    return (
        <div className="wom-movie-detail">
            {movie && casts && recommendations ? (
                <>
                    <div className="content">
                        <div className="movie-poster">
                            <LazyImage
                                src={movie.poster_path ? ENV.TMDB_IMAGE + movie.poster_path : imageNotFound}
                                placeholder={({ imageProps, ref }) => (
                                    <div className="isLoading" ref={ref}>
                                        <div className="loader poster-loader">
                                            Loading ...
                                        </div>
                                    </div>
                                )}
                                actual={({ imageProps }) => <img {...imageProps} />}
                            >
                                <img
                                    src={movie.poster_path ? ENV.TMDB_IMAGE + movie.poster_path : imageNotFound}
                                    alt={movie.title}
                                />
                            </LazyImage>
                        </div>
                        <div className="movie-detail">
                            <h3>{movie.title}</h3>
                            <p>{movie.tagline}</p>
                            <div className="information">
                                <div>
                                    <i className="material-icons">star</i>
                                    <span> {movie.vote_average}</span>
                                </div>
                                <span>
                                    {movie.runtime} MIN /{' '}
                                    {movie.original_language} /{' '}
                                    {movie.release_date}
                                </span>
                            </div>
                            <div className="genres">
                                <b>Genres</b>
                                <ul>
                                    {movie.genres.map((genre) => (
                                        <li>
                                            <Link to={WOMPath.movieList(genre.name)}>
                                                <i className="material-icons">
                                                    trip_origin
                                                </i>
                                                <span>{genre.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="synopsis">
                                <b>The synopsis</b>
                                <p>{movie.overview}</p>
                            </div>
                            {casts ? (
                                <div className="casts">
                                    <b>The Casts</b>
                                    <Swiper
                                        autoplay={{ delay: 4000 }}
                                        breakpoints={{
                                            320: {
                                                slidesPerView: 4,
                                            },
                                            480: {
                                                slidesPerView: 5,
                                            },
                                            640: {
                                                slidesPerView: 7,
                                            },
                                        }}
                                    >
                                        {casts.length
                                            ? casts.map((cast) => (
                                                  <SwiperSlide>
                                                      <div className="cast">
                                                          <Link to={WOMPath.person(cast.id.toString())}>
                                                              <LazyImage
                                                                  src={
                                                                      cast.profile_path ? ENV.TMDB_IMAGE + cast.profile_path : 'https://kctherapy.com/wp-content/uploads/2019/09/default-user-avatar-300x293.png'
                                                                  }
                                                                  placeholder={({ imageProps, ref }) => (
                                                                      <div className="isLoading" ref={ref}>
                                                                          <div className="loader poster-loader">
                                                                                Loading ...
                                                                          </div>
                                                                      </div>
                                                                  )}
                                                                  actual={({imageProps}) => <img {...imageProps}/>}
                                                              >
                                                                  <img
                                                                      src={cast.profile_path ? ENV.TMDB_IMAGE + cast.profile_path : imageNotFound}
                                                                      alt={cast.name}
                                                                  />
                                                              </LazyImage>
                                                          </Link>
                                                      </div>
                                                  </SwiperSlide>
                                              ))
                                            : null}
                                    </Swiper>
                                </div>
                            ) : null}
                            <div className="buttons">
                                <Button onClick={() => history.goBack()}>Back</Button>
                                {movie.homepage && (
                                    <Button onClick={() => window.open(movie.homepage)}>Website</Button>
                                )}
                                {movie.imdb_id && (
                                    <Button onClick={() => window.open(`https://www.imdb.com/title/${movie.imdb_id}`, '_blank')}>
                                        IMDB Page
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    {recommendations ? (
                        recommendations.length ? (
                            <div className="recommendations">
                                <h3>Recommendations</h3>
                                <div className="movie-list">
                                    {recommendations.map((movie) => (
                                        <MovieCard movie={movie} />
                                    ))}
                                    <div className="grid-protector"></div>
                                    <div className="grid-protector"></div>
                                    <div className="grid-protector"></div>
                                    <div className="grid-protector"></div>
                                </div>
                            </div>
                        ) : null
                    ) : null}
                </>
            ) : (
                <div className="movie-loader">
                    <div className="loader">Loading ...</div>
                </div>
            )}
        </div>
    );
};

export default MovieDetail;
